"use client";

import { useEffect, useMemo, useState } from "react";

type FeatureFlagPayload = {
  allEnabled: boolean;
  flags: Record<string, boolean>;
};

export const useFeatureFlags = () => {
  const [payload, setPayload] = useState<FeatureFlagPayload>({
    allEnabled: false,
    flags: {},
  });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch("/api/feature-flags");
        if (!res.ok) {
          throw new Error("Failed to load feature flags");
        }
        const data = (await res.json()) as FeatureFlagPayload;
        if (!cancelled) {
          setPayload(data);
        }
      } catch {
        if (!cancelled) {
          setPayload({ allEnabled: false, flags: {} });
        }
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const isEnabled = useMemo(() => {
    return (name?: string) => {
      if (!name) return true;
      if (payload.allEnabled) return true;
      return Boolean(payload.flags[name]);
    };
  }, [payload]);

  return { ready, isEnabled, flags: payload.flags, allEnabled: payload.allEnabled };
};
